import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Col, Form, Row, Table } from "react-bootstrap";
import { FaPen, FaPlus, FaTrash, FaUndo } from "react-icons/fa";
import { getCurrentUser, listEntityRows, setEstimatesMarkup } from "../../services/apiClient.js";
import { SortableRows, SortableTr } from "../SortableRows.jsx";
import { filterByWorkCentre, jobMarkup, jobProfitability, sumLines, workCentreBreakdown } from "./workCentreSummary.js";

// A reusable priced-line grid (legacy GPSEstimate / GPSEstimateAddition / GPSPCSum).
// Data-entry first: editing is INLINE (the row expands in place); saving a NEW line
// reopens a blank entry row so you can rattle off 50-100 lines without scrolling;
// per-row INSERT drops a line into the middle; Go-to jumps leap top/bottom.
//
// Money derives live and is recomputed server-side: gross = unit price × qty,
// markup = gross × %/100, sell = gross + markup. `is_header` is a bold grouping flag
// (header rows still carry money). Reused across the quote tab via props:
//   api   — { list, create, update, remove } bound to a resource
//   owner — optional extra leading select column, e.g. { field, label, options }
//           (the Variation a costing line belongs to)

const nzd = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });
const money = (v) => nzd.format(Number(v || 0));
const numOr0 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const REF_SELECTS = [
  { field: "floor_id", label: "Floor", key: "floors" },
  { field: "room_id", label: "Room", key: "rooms" },
  { field: "work_centre_id", label: "Work Centre", key: "work-centres" },
  { field: "cost_type_id", label: "Cost Type", key: "cost-types" },
];

const BASE_EMPTY = {
  is_header: false,
  description: "",
  notes: "",
  floor_id: "",
  room_id: "",
  work_centre_id: "",
  cost_type_id: "",
  quantity_type_id: "",
  unit_price: "",
  quantity: "",
  // Default markup 57% — legacy EST_MARGIN_PERCENT (QuotePage.aspx.cs:118),
  // re-confirmed by Karen 2026-07-15.
  margin_percent: "57",
};

// lean: drop the Floor/Room, Work Centre and Cost Type columns (for grids that are
// just description + pricing, e.g. the Final Quote override).
// Rows sharing a `pairField` value move as ONE unit when dragged (PC sums:
// the negative PC Sum + its Actual are inseparable — legacy LinkedEstimateIDs).
function regroupPairs(list, pairField, draggedId) {
  const seen = new Set();
  const out = [];
  const draggedGroup = list.find((x) => Number(x.id) === draggedId)?.[pairField] || null;
  for (const item of list) {
    const id = Number(item.id);
    if (seen.has(id)) continue;
    const group = item[pairField];
    if (!group) {
      out.push(item);
      seen.add(id);
      continue;
    }
    // The dragged row anchors its own pair — skip a partner encountered first
    // so the pair lands where the user DROPPED, not where the partner sat.
    if (group === draggedGroup && id !== draggedId && !seen.has(draggedId)) continue;
    const members = list.filter((x) => x[pairField] === group && !seen.has(Number(x.id)));
    members.sort((a, b) => (a.kind === b.kind ? 0 : a.kind === "Actual" ? 1 : -1)); // PC Sum first
    for (const m of members) {
      out.push(m);
      seen.add(Number(m.id));
    }
  }
  return out;
}

function PricedLineGrid({
  claimId, api, title = "Quote Lines", owner = null, lean = false, pairField = null,
  // PC Sums store their cost type in kind, rather than the lookup FK.
  costTypeField = "cost_type_name",
  // Per-row delete confirmation wording — PC Sums warn that the whole
  // Less/Actual pair goes (the server deletes both; deleting twice was the
  // old flow and half-deleted pairs the failure it invited).
  confirmDelete = null,
  // ACCEPTED = LOCKED (KS + Robert, 2026-08-25): once the job has its
  // go-ahead the quote is the agreed document — changes go through
  // Variations. Admins see the controls regardless (corrections happen);
  // the server enforces the same rule, this just stops the 400 round-trip.
  locked = false,
  // Karen 2026-07-15 (agreed): quote lines need a Work Centre + Quantity — a
  // missing WC is what later breaks auto-create-orders and blocks progress
  // claims. Header rows are exempt (grouping rows, like legacy).
  requireWorkCentreQty = false,
  // Slot for the host page to drop a control into this grid's toolbar (the
  // Quote tab puts "Create From Template" here). Given the grid's own `load` so
  // it can refresh the rows after it has changed them.
  headerExtra = null,
  // Set Markup (legacy Quotes-box "Set Margin for all quotes"): bulk re-markup
  // of every line EXCEPT PC Sums. Estimates grid only; admin/receptionist.
  allowSetMarkup = false,
  // Auto P&G/JMF fee lines (quotes.md §8) — estimates grid only. feeStatus
  // supplies each fee's state for the row badge, onFeeReset un-freezes an
  // edited line, onLinesChanged tells the host a reload happened (fee states
  // can move server-side on any save).
  feeStatus = null,
  onFeeReset = null,
  onLinesChanged = null,
  // Per-ROW fee state, for the costings grid: there the states live on each
  // line's variation HEADER, not on the claim (quotes.md 8.29). Given the
  // row, returns its state; absent, the claim-level feeStatus answers.
  // onFeeReset receives the row as its second argument either way, so the
  // host can address the right header.
  rowFeeState = null,
  // Bump to make the grid reload its rows after the host changed them
  // server-side (Restore / Enable on a variation header).
  reloadKey = 0,
  // The claim's profile default markup (fee-practice-analysis.md): AA-style
  // jobs price at 0% with explicit fees, Vero R&Q at 57. null = the global 57.
  defaultMarkup = null,
}) {
  const [items, setItems] = useState([]);
  const [lookups, setLookups] = useState({});
  const [editingId, setEditingId] = useState(null); // null | id | "new" | "insert"
  const [insertAfterId, setInsertAfterId] = useState(null);
  const [form, setForm] = useState(BASE_EMPTY);
  const [error, setError] = useState("");
  // Editor-row failures render IN the editor row, not the top alert: on a long
  // quote the entry row sits hundreds of rows below the alert, so a validation
  // message up there is off-screen and the save just looks broken (KS, 2026-08-19).
  const [formError, setFormError] = useState("");
  const [invalidField, setInvalidField] = useState(null);
  const [focusTick, setFocusTick] = useState(0);
  const firstFieldRef = useRef(null);
  const bottomRef = useRef(null);
  // Set Markup reveal (legacy pattern: link → inline % box + Update).
  const [canSetMarkup, setCanSetMarkup] = useState(false);
  const [markupOpen, setMarkupOpen] = useState(false);
  const [markupPct, setMarkupPct] = useState("57");
  const [wcFilter, setWcFilter] = useState(""); // "" = all work centres

  const markupDefault = defaultMarkup == null ? BASE_EMPTY.margin_percent : String(defaultMarkup);
  // A fresh entry on the costings grid starts under the NEWEST variation
  // header (the one just added is the one about to be costed) — picking it
  // by hand on every line was the annoyance (Robert 2026-09-05, job 76347).
  const blank = () => ({
    ...BASE_EMPTY,
    margin_percent: markupDefault,
    ...(owner ? { [owner.field]: owner.options[owner.options.length - 1]?.id ?? "" } : {}),
  });
  // The next entry after a save carries floor + room forward (Karen 2026-07-15:
  // lines come in room batches — no re-picking on every line) — and the
  // Variation it belongs to, for the same reason.
  const carryOn = (justSaved) => ({
    ...blank(),
    floor_id: justSaved.floor_id || "",
    room_id: justSaved.room_id || "",
    ...(owner ? { [owner.field]: justSaved[owner.field] || "" } : {}),
  });
  const ownerLabel = (id) =>
    owner ? owner.options.find((o) => Number(o.id) === Number(id))?.name : null;

  function load() {
    return api
      .list(claimId)
      .then((rows) => {
        setItems(rows);
        onLinesChanged?.(rows);
      })
      .catch(() => setError("Unable to load lines."));
  }
  useEffect(() => {
    load();
    Promise.all(
      ["floors", "rooms", "work-centres", "cost-types", "quantity-types"].map((k) =>
        listEntityRows(k)
          .then((rows) => [k, rows])
          .catch(() => [k, []])
      )
    ).then((pairs) => setLookups(Object.fromEntries(pairs)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  useEffect(() => {
    if (reloadKey) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  useEffect(() => {
    if (editingId) firstFieldRef.current?.focus();
  }, [focusTick, editingId]);

  // Set Markup is admin/receptionist only (as 1.0 — server enforces too).
  useEffect(() => {
    if (!allowSetMarkup) return;
    getCurrentUser()
      .then((me) =>
        setCanSetMarkup(!!me?.is_admin || /(^|;)\s*receptionist\s*(;|$)/i.test(me?.member_of || ""))
      )
      .catch(() => {});
  }, [allowSetMarkup]);

  async function applyMarkup() {
    setError("");
    try {
      const r = await setEstimatesMarkup(claimId, Number(markupPct));
      setMarkupOpen(false);
      await load();
      if (r.updated === 0) setError("No lines to update.");
    } catch (err) {
      setError(err.response?.data?.error || "Unable to set markups.");
    }
  }

  const preview = useMemo(() => {
    const gross = numOr0(form.unit_price) * numOr0(form.quantity);
    const margin = (gross * numOr0(form.margin_percent)) / 100;
    return { gross, margin, net: gross + margin };
  }, [form.unit_price, form.quantity, form.margin_percent]);

  // Work Centre filter (Robert 2026-08-03 — 1.0 put this breakdown in a table
  // under the quote lines; this answers the same question in the column itself).
  // "" = every line, and that path is untouched: same rows, same order, headers
  // in place, totals as before.
  const centres = useMemo(() => workCentreBreakdown(items), [items]);
  const visible = useMemo(() => filterByWorkCentre(items, wcFilter), [items, wcFilter]);
  // Line numbers stay the numbers from the WHOLE quote — seeing 3, 7, 12 is how
  // you relate a filtered view back to the full list; renumbering them 1, 2, 3
  // would quietly invent a different quote.
  const indexById = useMemo(() => new Map(items.map((it, i) => [it.id, i])), [items]);

  const totals = useMemo(() => sumLines(visible), [visible]);
  const totalMarkupPct = totals.gross ? (totals.margin / totals.gross) * 100 : null;
  // Internal, whole-quote, estimates grid only (feeStatus is its marker) —
  // hidden while filtered so a partial list never wears a whole-quote number.
  const profitability = useMemo(
    () => (feeStatus && !wcFilter ? jobProfitability(items) : null),
    [feeStatus, wcFilter, items]
  );
  const totalMarkup = useMemo(
    () => (feeStatus && !wcFilter ? jobMarkup(items) : null),
    [feeStatus, wcFilter, items]
  );
  const activeCentre = wcFilter ? centres.find((c) => c.key === wcFilter) : null;

  // Where a NEW line will land, so the entry row sits there while it is being
  // typed: after the last ordinary row of its block — the whole list on the
  // quote grid, the picked Variation's rows on the costings grid — and AHEAD
  // of the pinned fee lines, which otherwise sat between the entry row and the
  // line it belongs with (Robert 2026-09-05, job 76347: "should be item 2 on
  // the list"). The row follows the Variation picker live, so changing it
  // shows where the line is going. No block to join, or nothing pinned after
  // it = the bottom, as before. A costing with no Variation lands at the
  // bottom too (nothing pins under it).
  const newAfterId = useMemo(() => {
    if (editingId !== "new") return null;
    const ownerVal = owner ? String(form[owner.field] || "") : "";
    if (owner && !ownerVal) return null;
    let last = -1;
    for (let i = visible.length - 1; i >= 0; i -= 1) {
      const it = visible[i];
      if (it.line_kind) continue;
      if (ownerVal && String(it[owner.field] || "") !== ownerVal) continue;
      last = i;
      break;
    }
    if (last < 0 || last === visible.length - 1) return null;
    return visible[last].id;
  }, [editingId, visible, form, owner]);

  const set = (patch) => {
    setForm((c) => ({ ...c, ...patch }));
    if (invalidField && patch[invalidField] !== undefined) {
      setInvalidField(null);
      setFormError("");
    }
  };
  const goTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const goBottom = () => bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });

  function openNew() {
    setError("");
    setFormError("");
    setInvalidField(null);
    setForm(blank());
    setInsertAfterId(null);
    setEditingId("new");
    setFocusTick((t) => t + 1);
  }
  function openInsert(it) {
    setError("");
    setFormError("");
    setInvalidField(null);
    // Inserting after a row = joining its block: same Variation as that row.
    // (Carrying its floor and room too — Karen Renall 2026-09-10 — is an upgrade waiting on KS.)
    setForm({ ...blank(), ...(owner ? { [owner.field]: it[owner.field] || "" } : {}) });
    setInsertAfterId(it.id);
    setEditingId("insert");
    setFocusTick((t) => t + 1);
  }
  function openEdit(it) {
    setError("");
    setFormError("");
    setInvalidField(null);
    setInsertAfterId(null);
    setForm({
      is_header: !!it.is_header,
      description: it.description || "",
      notes: it.notes || "",
      floor_id: it.floor_id || "",
      room_id: it.room_id || "",
      work_centre_id: it.work_centre_id || "",
      cost_type_id: it.cost_type_id || "",
      ...(costTypeField === "kind" ? { kind: it.kind || "PC Sum" } : {}),
      quantity_type_id: it.quantity_type_id || "",
      unit_price: it.unit_price ?? "",
      quantity: it.quantity ?? "",
      margin_percent: it.margin_percent ?? "",
      ...(owner ? { [owner.field]: it[owner.field] || "" } : {}),
    });
    setEditingId(it.id);
    setFocusTick((t) => t + 1);
  }
  function close() {
    setEditingId(null);
    setInsertAfterId(null);
    setFormError("");
    setInvalidField(null);
  }
  async function save() {
    setFormError("");
    setInvalidField(null);
    if (requireWorkCentreQty && !form.is_header) {
      if (!form.work_centre_id) {
        setFormError("Work Centre is required on quote lines.");
        setInvalidField("work_centre_id");
        return;
      }
      if (!(Number(form.quantity) > 0)) {
        setFormError("Quantity is required on quote lines.");
        setInvalidField("quantity");
        return;
      }
    }
    try {
      if (editingId === "new") {
        await api.create(claimId, form);
        await load();
        setForm(carryOn(form));
        setFocusTick((t) => t + 1);
      } else if (editingId === "insert") {
        const created = await api.create(claimId, { ...form, after_id: insertAfterId });
        await load();
        setInsertAfterId(created.id);
        setForm(carryOn(form));
        setFocusTick((t) => t + 1);
      } else {
        await api.update(claimId, editingId, form);
        await load();
        close();
      }
    } catch (err) {
      setFormError(err.response?.data?.error || "Unable to save line.");
    }
  }
  async function remove(it) {
    if (!window.confirm(confirmDelete?.(it) || "Delete this line?")) return;
    setError("");
    try {
      await api.remove(claimId, it.id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to delete.");
    }
  }

  // Drag-and-drop: apply optimistically, persist the full order, revert on failure.
  // With pairField set, dragging one row of a pair moves BOTH (see regroupPairs).
  // Never drag while filtered: handleReorder posts the FULL id list and the
  // server rewrites display_order from the array positions, so reordering a
  // visible subset would renumber the quote using only the rows on screen and
  // silently scramble everything hidden.
  const dragDisabled =
    locked || editingId !== null || typeof api.reorder !== "function" || wcFilter !== "";
  async function handleReorder(reordered, draggedId) {
    const prev = items;
    const next = pairField ? regroupPairs(reordered, pairField, draggedId) : reordered;
    setItems(next);
    try {
      await api.reorder(claimId, next.map((l) => l.id));
    } catch (err) {
      setItems(prev);
      setError(err.response?.data?.error || "Unable to save the new order.");
    }
  }

  // Drag + # + Description + Unit/Qty/%Mk + Gross/Markup/Sell + Actions = 10; plus the
  // owner column and the 3 reference columns (Floor/Room, Work Centre, Cost Type) when shown.
  const colCount = 10 + (owner ? 1 : 0) + (lean ? 0 : 3);

  function selectField(value, onChange, options, opts = {}) {
    return (
      <ThemedSelect size="sm" className={opts.className} ref={opts.withRef ? firstFieldRef : undefined} value={value} onChange={onChange}>
        <option value="">{opts.placeholder ?? "— None —"}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </ThemedSelect>
    );
  }

  function editorRow(key, mode) {
    const labels = {
      new: ["Add line", "Done"],
      insert: ["Insert", "Done"],
      edit: ["Save", "Cancel"],
    }[mode];
    return (
      <tr key={key}>
        <td colSpan={colCount} className="bg-body-tertiary">
          {mode === "insert" ? (
            <div className="small text-muted mb-1">Inserting a line here ↡</div>
          ) : null}
          <Row className="g-2 align-items-end">
            {owner ? (
              <Col xs={12} md={3}>
                <Form.Label className="mb-0 small fw-semibold">{owner.label}</Form.Label>
                {selectField(form[owner.field], (e) => set({ [owner.field]: e.target.value }), owner.options, {
                  withRef: true,
                  placeholder: "— None —",
                })}
              </Col>
            ) : null}
            {!lean
              ? REF_SELECTS.map((s, i) => (
                  <Col xs={6} md={3} key={s.field}>
                    <Form.Label className="mb-0 small fw-semibold">
                      {s.label}
                      {s.field === "work_centre_id" && requireWorkCentreQty && !form.is_header ? (
                        <span className="text-danger"> *</span>
                      ) : null}
                    </Form.Label>
                    {s.field === "cost_type_id" && costTypeField === "kind" ? (
                      <Form.Control size="sm" value={form.kind || "PC Sum"} readOnly />
                    ) : selectField(form[s.field], (e) => set({ [s.field]: e.target.value }), lookups[s.key] || [], {
                      withRef: !owner && i === 0,
                      className: invalidField === s.field ? "is-invalid" : undefined,
                    })}
                  </Col>
                ))
              : null}
            <Col xs={12} md={8}>
              <Form.Label className="mb-0 small fw-semibold">Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                size="sm"
                ref={lean && !owner ? firstFieldRef : undefined}
                value={form.description}
                onChange={(e) => set({ description: e.target.value })}
              />
            </Col>
            <Col xs={12} md={4}>
              <Form.Label className="mb-0 small fw-semibold">Notes (internal)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                size="sm"
                value={form.notes}
                onChange={(e) => set({ notes: e.target.value })}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="mb-0 small fw-semibold">Unit Price</Form.Label>
              <Form.Control size="sm" type="number" step="0.01" value={form.unit_price}
                onChange={(e) => set({ unit_price: e.target.value })} />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="mb-0 small fw-semibold">
                Quantity
                {requireWorkCentreQty && !form.is_header ? (
                  <span className="text-danger"> *</span>
                ) : null}
              </Form.Label>
              <Form.Control size="sm" type="number" step="0.01" value={form.quantity}
                className={invalidField === "quantity" ? "is-invalid" : undefined}
                onChange={(e) => set({ quantity: e.target.value })} />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="mb-0 small fw-semibold">Unit</Form.Label>
              {selectField(form.quantity_type_id, (e) => set({ quantity_type_id: e.target.value }), lookups["quantity-types"] || [], { placeholder: "—" })}
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="mb-0 small fw-semibold">% Markup</Form.Label>
              <Form.Control size="sm" type="number" step="0.01" value={form.margin_percent}
                onChange={(e) => set({ margin_percent: e.target.value })} />
            </Col>
            <Col xs={12} md={4} className="d-flex align-items-center">
              <Form.Check
                id={`pl-hdr-${key}`}
                className="mt-3 small"
                label="Section header"
                checked={form.is_header}
                onChange={(e) => set({ is_header: e.target.checked })}
              />
            </Col>
            {formError ? (
              <Col xs={12}>
                <div className="alert alert-danger py-1 px-2 mb-0 small">{formError}</div>
              </Col>
            ) : null}
            <Col xs={12} className="d-flex flex-wrap align-items-center gap-2">
              <span className="small text-muted">
                Gross <strong>{money(preview.gross)}</strong> · Markup{" "}
                <strong>{money(preview.margin)}</strong> · Sell{" "}
                <strong className="text-body">{money(preview.net)}</strong>
              </span>
              <span className="ms-auto" />
              <Button size="sm" onClick={save}>{labels[0]}</Button>
              <Button size="sm" variant="secondary" onClick={close}>{labels[1]}</Button>
            </Col>
          </Row>
        </td>
      </tr>
    );
  }

  function displayRow(it, idx) {
    const feeState = it.line_kind
      ? (rowFeeState ? rowFeeState(it) : feeStatus?.[it.line_kind]?.state)
      : null;
    return (
      <SortableTr key={it.id} id={it.id} disabled={dragDisabled || !!it.line_kind} className={it.is_header ? "table-surface" : undefined}>
        <td className="text-muted">{idx + 1}</td>
        {owner ? (
          <td className="small">{ownerLabel(it[owner.field]) || <span className="text-muted">—</span>}</td>
        ) : null}
        {!lean ? (
          <td className="small">
            {[it.floor_name, it.room_name].filter(Boolean).join(" / ") || <span className="text-muted">—</span>}
          </td>
        ) : null}
        {!lean ? (
          <td className="small">{it.work_centre_name || <span className="text-muted">—</span>}</td>
        ) : null}
        {/* overflowWrap: a pasted link cannot wrap on its own, and one 255-character URL stretched the
            whole grid past the screen (76538, 2026-09-10) */}
        <td className={it.is_header ? "fw-semibold" : undefined} style={{ whiteSpace: "pre-wrap", minWidth: 180, overflowWrap: "anywhere" }}>
          {/* Fee lines wear their state so nobody wonders why a line they
              didn't type is here, or why an edited one stopped following the
              quote (quotes.md §8). */}
          {it.line_kind ? (
            <Badge
              bg={feeState === "auto" ? "info" : "secondary"}
              className="me-1 align-text-bottom"
              data-hint={
                ({ pg: "Preliminaries & General — ",
                   jmf: "Job Management Fee — ",
                   profit: "Builders Overheads and Profit — ",
                   rubbish: "Rubbish Removal / Disposal — " }[it.line_kind] || "") +
                (feeState === "auto"
                  ? "calculated automatically, recalculates as the quote changes"
                  : "edited by hand — no longer recalculates (Reset puts it back on auto)")
              }
            >
              {{ pg: "P&G", jmf: "JMF", profit: "Profit", rubbish: "Rubbish" }[it.line_kind] || it.line_kind}{feeState === "auto" ? " · auto" : " · edited"}
            </Badge>
          ) : null}
          {it.description || <span className="text-muted">—</span>}
          {/* Notes (internal) were saving all along — 5,192 lines carry one —
              but nothing ever displayed them, so typing one and saving looked
              like it had been thrown away (Karen Renall 2026-08-05). They hold
              the estimator's working ("12.52m2", "6m"), so they belong under
              the line they explain rather than in a column of their own on an
              already wide grid. Internal: the printed quote does not show them. */}
          {it.notes ? (
            <div className="small text-muted fst-italic">{it.notes}</div>
          ) : null}
        </td>
        {!lean ? (
          <td className="small">{it[costTypeField] || <span className="text-muted">—</span>}</td>
        ) : null}
        {/* cd-entered: the typed figures in the theme's primary colour (app.css). */}
        <td className="text-end text-nowrap cd-entered">{money(it.unit_price)}</td>
        <td className="text-end text-nowrap cd-entered">
          {Number(it.quantity)}
          {/* The unit keeps the cell's colour (a muted grey beside a blue number
              looked odd — Robert 2026-09-11); it is quieter by opacity instead. */}
          {it.quantity_type_name ? <span className="cd-entered-unit"> {it.quantity_type_name}</span> : null}
        </td>
        <td className="text-end cd-entered">{Number(it.margin_percent)}%</td>
        <td className="text-end text-nowrap">{money(it.gross_value)}</td>
        <td className="text-end text-nowrap">{money(it.margin_value)}</td>
        <td className="text-end text-nowrap fw-semibold">{money(it.net_value)}</td>
        <td className="text-end text-nowrap">
          {locked ? null : (
          <>
          {/* Icons, not words: three labelled buttons took 230px of a grid whose
              description column was starved for it (KS 2026-09-11). Each carries
              its name as a hint. */}
          <Button variant="outline-primary" size="sm" className="me-1 px-2" data-hint="Edit this line" aria-label="Edit" onClick={() => openEdit(it)}><FaPen /></Button>
          {/* Insert-after makes no sense on a pinned fee row; Reset shows only
              once an edit froze the line — users make mistakes (Robert). */}
          {it.line_kind ? (
            feeState === "frozen" && onFeeReset ? (
              <Button
                variant="outline-secondary" size="sm" className="me-1 px-2"
                data-hint="Back to automatic — recalculates from the quote" aria-label="Reset"
                onClick={async () => { await onFeeReset(it.line_kind, it); await load(); }}
              ><FaUndo /></Button>
            ) : null
          ) : (
            <Button variant="outline-secondary" size="sm" className="me-1 px-2" data-hint="Insert a line after this one" aria-label="Insert" onClick={() => openInsert(it)}><FaPlus /></Button>
          )}
          <Button variant="outline-danger" size="sm" className="px-2" data-hint="Delete this line" aria-label="Delete" onClick={() => remove(it)}><FaTrash /></Button>
          </>
          )}
        </td>
      </SortableTr>
    );
  }

  const longList = items.length > 8;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">{title}</h5>
        <div className="d-flex gap-2 align-items-center">
          {allowSetMarkup && canSetMarkup ? (
            markupOpen ? (
              <div className="d-flex gap-1 align-items-center">
                <span className="small text-muted text-nowrap">Markup % (PC Sums excluded)</span>
                <Form.Control
                  size="sm"
                  type="number"
                  style={{ width: 80 }}
                  value={markupPct}
                  autoFocus
                  onChange={(e) => setMarkupPct(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && applyMarkup()}
                />
                <Button size="sm" onClick={applyMarkup}>Update</Button>
                <Button size="sm" variant="secondary" onClick={() => setMarkupOpen(false)}>Cancel</Button>
              </div>
            ) : (
              <Button size="sm" variant="outline-secondary"
                onClick={() => { setMarkupPct(markupDefault); setMarkupOpen(true); }}>
                Set Markup
              </Button>
            )
          ) : null}
          {typeof headerExtra === "function" ? headerExtra({ load, items, lookups }) : headerExtra}
          {longList ? (
            <Button size="sm" variant="outline-secondary" onClick={goBottom}>↓ Bottom</Button>
          ) : null}
          {locked ? (
            <span className="text-muted small">Accepted — quote locked. Changes go through Variations.</span>
          ) : editingId === null ? (
            <Button size="sm" variant="success" onClick={openNew}>+ Add Line</Button>
          ) : null}
        </div>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      <SortableRows items={visible} onReorder={handleReorder} disabled={dragDisabled}>
      <Table bordered hover responsive size="sm" className="mb-0 align-middle">
        <thead className="table-surface">
          <tr>
            <th style={{ width: 36 }} />
            <th style={{ width: 34 }}>#</th>
            {owner ? <th>{owner.label}</th> : null}
            {!lean ? <th>Floor / Room</th> : null}
            {!lean ? (
              <th style={{ minWidth: 150 }}>
                Work Centre
                {/* The filter lives IN the column (Robert 2026-08-03). Each option
                    carries that centre's cost total, so opening the dropdown IS
                    the breakdown 1.0 printed under the lines — you can read the
                    whole split without choosing anything. Hidden when there is
                    nothing to choose between. */}
                {centres.length > 1 ? (
                  <ThemedSelect
                    size="sm"
                    className="mt-1 fw-normal"
                    value={wcFilter}
                    onChange={(e) => setWcFilter(e.target.value)}
                    data-hint="Amounts show cost before markup. Select a work centre to filter the lines and totals."
                  >
                    <option value="">All work centres ({centres.length})</option>
                    {centres.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.name} — {money(c.gross)} cost
                      </option>
                    ))}
                  </ThemedSelect>
                ) : null}
              </th>
            ) : null}
            {/* The widest thing on the grid by right (KS 2026-09-11: "make the
                description column wider, Actions can be smaller"). */}
            <th style={{ width: "40%", minWidth: 260 }}>Description</th>
            {!lean ? <th>Cost Type</th> : null}
            <th className="text-end">Unit Price</th>
            <th className="text-end">Qty</th>
            <th className="text-end">% Mk</th>
            <th className="text-end">Gross</th>
            <th className="text-end">Markup</th>
            <th className="text-end">Sell</th>
            <th className="text-end" style={{ width: 112 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {visible.flatMap((it) => {
            const idx = indexById.get(it.id) ?? 0;
            const rows = [editingId === it.id ? editorRow(it.id, "edit") : displayRow(it, idx)];
            if (editingId === "insert" && insertAfterId === it.id) {
              rows.push(editorRow(`ins-${it.id}`, "insert"));
            }
            if (editingId === "new" && newAfterId === it.id) {
              rows.push(editorRow("new", "new"));
            }
            return rows;
          })}
          {editingId === "new" && newAfterId == null ? editorRow("new", "new") : null}
          {visible.length === 0 && editingId !== "new" ? (
            <tr>
              <td colSpan={colCount} className="text-center text-muted">
                {wcFilter
                  ? "No lines in this work centre."
                  : feeStatus && ["pg", "jmf", "profit", "rubbish"].some((k) => feeStatus[k]?.enabled)
                    // The pricing-profile header above PROMISES automatic fees;
                    // this says when they arrive (Robert 2026-08-25: a newbie
                    // shouldn't wonder why the promised lines aren't here).
                    ? "No lines yet — add the priced work and the automatic fee lines above appear with it."
                    : "No lines."}
              </td>
            </tr>
          ) : null}
        </tbody>
        {visible.length > 0 ? (
          <tfoot>
            <tr className="table-surface">
              <td colSpan={colCount - 7} className="text-start fw-semibold">
                {/* Say WHICH totals these are. A filtered quote showing a
                    smaller number with the word "Totals" beside it is the one
                    way this feature could actually mislead someone. */}
                {activeCentre ? (
                  <>
                    Totals — {activeCentre.name}
                    <span className="fw-normal text-muted ms-2">
                      {activeCentre.count} of {items.length} lines · whole quote {money(sumLines(items).net)}
                    </span>
                  </>
                ) : (
                  "Totals"
                )}
              </td>
              <td />
              <td />
              <td className="text-end text-nowrap fw-semibold">
                {totalMarkupPct == null ? "—" : `${totalMarkupPct.toFixed(1)}%`}
              </td>
              <td className="text-end text-nowrap fw-semibold">{money(totals.gross)}</td>
              <td className="text-end text-nowrap fw-semibold">{money(totals.margin)}</td>
              <td className="text-end text-nowrap fw-semibold">{money(totals.net)}</td>
              {/* Allowed to WRAP: on one line this cell held the Actions column
                  open at ~160px, width the description was short of (Robert
                  2026-09-11). Two short lines cost nothing in a totals row. */}
              <td className="text-start">
                {/* BOTH dialects, labelled like every margin calculator
                    (Robert 2026-08-21, after KS added the fee badges to ~53
                    and the footer answered 34.8 — same money, margin-of-sell
                    vs markup-on-cost): never the bare word "profit" again. */}
                {profitability != null ? (
                  <span
                    className="small text-muted"
                    data-hint="Margin = (sell − ordinary-line cost) ÷ sell. Markup = the same profit over cost. Auto fee lines count as profit; hand-entered fee lines count as cost. Internal only — target is around 53%."
                  >
                    <span className="text-nowrap">Margin {profitability.toFixed(1)}%</span>
                    {totalMarkup != null ? <> · <span className="text-nowrap">Markup {totalMarkup.toFixed(1)}%</span></> : null}
                  </span>
                ) : null}
              </td>
            </tr>
          </tfoot>
        ) : null}
      </Table>
      </SortableRows>
      <div ref={bottomRef} />
      {longList ? (
        <div className="mt-2 text-end">
          <Button size="sm" variant="outline-secondary" onClick={goTop}>↑ Top of page</Button>
        </div>
      ) : null}
    </>
  );
}

export default PricedLineGrid;
